import {
  Box,
  Stack,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DatePickerSlotProps } from '@mui/x-date-pickers/DatePicker/DatePicker.types';
import { TimePicker, TimePickerSlotProps } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs, UnitType } from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, ControllerFieldState, FormProvider, useForm } from 'react-hook-form';
import { FormSwitch } from 'src/components/Forms/FormSwitch/form-switch';

export type DateRangeValue = { from: Dayjs; to: Dayjs };

export type DateRangeProps = {
  onChange: (dateRange: DateRangeValue) => void;
  value?: DateRangeValue;
  size?: TextFieldProps['size'];
};

const dateRangeGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gridTemplateAreas: '"startDate to endDate" "startTime . endTime"',
  alignItems: 'center',
  gap: 1,
};

type Fields = {
  startDate: Dayjs;
  startTime: Dayjs;
  endDate: Dayjs;
  endTime: Dayjs;
  isAllDay: boolean;
};

export const DateRange = ({ onChange, value, size }: DateRangeProps) => {
  const today = useMemo(() => dayjs(), []);

  // `useForm` to maintain internal state of the component. It doesn't submit anything.
  const methods = useForm<Fields>({
    defaultValues: {
      startDate: value?.from || today,
      startTime: value?.from || today,
      endDate: value?.to || today,
      endTime: value?.to || today,
      isAllDay: isAllDay(value),
    },
    mode: 'all',
  });
  const { control, watch, trigger, setValue } = methods;

  const fields = watch();

  useEffect(() => {
    void trigger();
    onChange(buildDateRange(fields));
  }, [trigger, fields, onChange]);

  const onStartDateChange = useCallback(
    (originalFn: (a: Dayjs | null) => void) => (value: Dayjs | null) => {
      // When `startDate` is changed to be greater that `endDate` (invalid state), we update `endDate` automatically
      // to equal `startDate`.
      if (value?.isAfter(fields.endDate)) {
        setValue('endDate', value);
      }
      originalFn(value);
    },
    [fields.endDate, setValue],
  );

  const validate = useCallback(
    (fieldName: Exclude<keyof Fields, 'isAllDay'>) => (value: Dayjs, formFields: Fields) => {
      switch (fieldName) {
        case 'endDate':
          if (value.isBefore(formFields.startDate)) {
            return 'End date must be after start date';
          }
          break;
        case 'endTime':
          if (value.isBefore(formFields.startTime)) {
            return 'End time must be after start time';
          }
          break;
      }
      return true;
    },
    [],
  );

  const commonDatePickerSlotsProps = useCallback(
    (fieldState: ControllerFieldState): DatePickerSlotProps<Dayjs, false> => ({
      textField: {
        error: !!fieldState.error,
        size,
        fullWidth: true,
      },
    }),
    [size],
  );

  const commonTimePickerSlotsProps = useCallback(
    (fieldState: ControllerFieldState): TimePickerSlotProps<Dayjs, false> => ({
      textField: {
        error: !!fieldState.error,
        size,
        fullWidth: true,
      },
    }),
    [size],
  );

  return (
    <FormProvider {...methods}>
      <Stack spacing={2}>
        <Box
          sx={[
            dateRangeGridSx,
            fields.isAllDay && { gridTemplateAreas: '"startDate to endDate"' },
          ]}
        >
          <Box gridArea="startDate">
            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  onChange={onStartDateChange(field.onChange)}
                  slots={{ textField: TextField }}
                  slotProps={commonDatePickerSlotsProps(fieldState)}
                />
              )}
            />
          </Box>
          {!fields.isAllDay && (
            <Box gridArea="startTime">
              <Controller
                name="startTime"
                control={control}
                render={({ field, fieldState }) => (
                  <TimePicker
                    {...field}
                    slots={{ textField: TextField }}
                    slotProps={commonTimePickerSlotsProps(fieldState)}
                    ampm={false}
                  />
                )}
              />
            </Box>
          )}
          <Typography gridArea="to">to</Typography>
          <Box gridArea="endDate">
            <Controller
              name="endDate"
              control={control}
              rules={{
                validate: validate('endDate'),
              }}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  slots={{ textField: TextField }}
                  slotProps={commonDatePickerSlotsProps(fieldState)}
                />
              )}
            />
          </Box>
          {!fields.isAllDay && (
            <Box gridArea="endTime">
              <Controller
                name="endTime"
                control={control}
                rules={{
                  validate: validate('endTime'),
                }}
                render={({ field, fieldState }) => (
                  <TimePicker
                    {...field}
                    slots={{ textField: TextField }}
                    slotProps={commonTimePickerSlotsProps(fieldState)}
                    ampm={false}
                  />
                )}
              />
            </Box>
          )}
        </Box>
        <FormSwitch type="switch" name="isAllDay" label="All day" />
      </Stack>
    </FormProvider>
  );
};

function updateDateWith(date: Dayjs, nextDate: Dayjs, units: UnitType[] = []): Dayjs {
  return units.reduce((acc, unit) => acc.set(unit, nextDate.get(unit)), date);
}

function setTimePart(date: Dayjs, time: Dayjs): Dayjs {
  return updateDateWith(date, time, ['h', 'm', 's']);
}

function buildDateRange({
  startDate,
  startTime,
  endDate,
  endTime,
  isAllDay,
}: Fields): DateRangeValue {
  if (isAllDay) {
    return { from: startDate.startOf('day'), to: endDate.endOf('day') };
  }
  return { from: setTimePart(startDate, startTime), to: setTimePart(endDate, endTime) };
}

function isAllDay(value: DateRangeValue | null | undefined) {
  return (
    value?.from?.isSame(value?.from?.startOf('day')) &&
    value?.to?.isSame(value?.to?.endOf('day'))
  );
}
